import React, { memo } from 'react';
import { Flex } from 'antd-mobile';
import PropTypes from 'prop-types';
import { ImgWrap } from 'pubBiz';
import { ClassifyRecommandCell, GoodsCell } from 'pubBiz/CommonCell';
import cx from 'classnames';
import styles from './index.modules.less';

const defaultProps = {
  data: {},
  productParams: {},
  onPress: null,
  productOnPress: null
};

// 商品信息数据结构
const itemInfoPropTypes = PropTypes.shape({
  id: PropTypes.string, // 商品ID
  itemId: PropTypes.string, // 商品ID（兼容字段）
  imgUrl: PropTypes.string, // 商品图片
  name: PropTypes.string, // 商品名称
  price: PropTypes.number, // 商品价格
  maxPrice: PropTypes.number, // 商品最高价格
  type: PropTypes.number // 商品类型（6：多规格商品）
});

// 卖点数据结构
const sellPointPropTypes = PropTypes.shape({
  sellPointColour: PropTypes.string, // 卖点颜色（支持'FF0000'或'#FF0000'格式）
  sellPointName: PropTypes.string // 卖点文案
});

// classifyItemRelationRespDtoList成员结构
const classifyItemRelationRespDtoPropTypes = PropTypes.arrayOf(
  PropTypes.shape({
    itemInfo: itemInfoPropTypes, // 商品信息
    sellPointList: PropTypes.arrayOf(sellPointPropTypes) // 卖点列表
  })
);

// classifyLinkRelationRespDtoList成员结构
const classifyLinkRelationRespDtoPropTypes = PropTypes.arrayOf(
  PropTypes.shape({
    id: PropTypes.string, // 推荐位ID
    type: PropTypes.number, // 推荐位类型
    content: PropTypes.string, // 推荐位内容
    extend: PropTypes.object, // 扩展字段
    imgUrlApp: PropTypes.string // 推荐位图片
  })
);

// data成员结构
const dataPropTypes = PropTypes.shape({
  mainImgUrl: PropTypes.string, // 背景图
  title: PropTypes.string, // 主标题
  titleColor: PropTypes.string, // 标题颜色
  moreText: PropTypes.string, // 进入标识
  moreColor: PropTypes.string, // 进入标识颜色
  filletValue: PropTypes.number, // 圆角值
  relativeType: PropTypes.number, // 内容目录 0: 产品目录 1: 推荐链接目录
  classifyItemRelationRespDtoList: classifyItemRelationRespDtoPropTypes, // 商品列表
  classifyLinkRelationRespDtoList: classifyLinkRelationRespDtoPropTypes // 推荐位列表
});

const propTypes = {
  width: PropTypes.number.isRequired, // 容器宽度
  data: dataPropTypes,
  productOnPress: PropTypes.func // 商品点击事件
};

const Bg_3Grid = memo((props) => {
  const {
    width: cellWidth, // Any 容器宽度
    data, // Object 数据
    productParams, // Object 商品参数
    onPress // Function 点击事件
  } = props;

  const {
    dynamicEffect = 1, // Number 显示动效 1: 固定展示 2: 自动轮播
    mainImgUrl, // String 背景图
    title, // String 主标题
    titleColor, // String 标题颜色
    moreText, // String 进入标识
    moreColor, // String 进入标识颜色
    filletValue = 0, // Number 圆角值
    relativeType, // Number 内容目录 0: 产品目录 1: 推荐链接目录
    classifyItemRelationRespDtoList, // Array 产品列表
    classifyLinkRelationRespDtoList // Array 推荐位
  } = data;

  const showDynamicEffect = dynamicEffect == 2;

  // 商品列表
  const renderProducts = () => {
    return (
      <Flex align="center" justify="center" className={styles.productList}>
        <GoodsCell
          autoplay={showDynamicEffect}
          infinite={showDynamicEffect}
          autoplayIntervalProp={4000}
          width={`calc(${cellWidth} - 0.48rem)`}
          data={{ itemList: classifyItemRelationRespDtoList }}
          showPoints={false}
          horizontal
          productParams={productParams}
          horizontalConfig={{
            size: '2.1rem',
            count: 3,
            showBullets: true,
            bulletsConfig: {
              marginTop: '0.3rem'
            }
          }}
        />
      </Flex>
    );
  };

  // 推荐位列表
  const renderRecommands = () => {
    // 推荐位图片的尺寸为 218 * 356
    // Todos: classifyLinkRelationRespDtoList filter 掉 displayMode === 1 是后端未过滤小程序展示内容做的保底，当后端修复这个缺陷后可以去掉该逻辑

    return (
      <Flex justify="center" className={styles.recommandList}>
        <ClassifyRecommandCell
          autoplay={showDynamicEffect}
          infinite={showDynamicEffect}
          autoplayIntervalProp={4000}
          width={`calc(${cellWidth} - 0.48rem)`}
          data={{
            itemList: (classifyLinkRelationRespDtoList || []).filter((o) => !(o.displayMode === 1))
          }}
          horizontal
          horizontalConfig={{
            size: {
              width: '2.1rem',
              height: `calc(2.2rem / 218 * 356)`
            },
            count: 3,
            showBullets: true,
            bulletsConfig: {
              marginTop: '0.12rem'
            },
            type: 'swiper'
          }}
          onPress={(val) => {
            onPress && onPress(val);
          }}
        />
      </Flex>
    );
  };

  const renderHeader = () => {
    return (
      <Flex
        direction="row"
        align="center"
        justify="between"
        className={styles.headerContainer}
        onClick={() => onPress && onPress(data)}>
        <div className={styles.title} style={{ color: titleColor }}>
          <span>{title || ''}</span>
        </div>
        {!!moreText && (
          <div className={styles.more} style={{ color: moreColor }}>
            <span>{`${moreText || ''}`}</span>
            <span
              className={cx('iconfont', 'icon-arrow-right', styles.arrowRightIcon)}
              style={{ color: moreColor }}></span>
          </div>
        )}
      </Flex>
    );
  };

  const renderContent = () => {
    switch (relativeType) {
      case 0:
        return renderProducts();
      case 1:
        return renderRecommands();
    }
  };

  return (
    // 背景图尺寸 702*478
    <Flex
      className={styles.wrapper}
      style={{
        borderRadius: `${(filletValue * 2) / 100}rem`
      }}>
      <ImgWrap w={cellWidth} h={`calc(${cellWidth} * 478 / 702)`} src={mainImgUrl || ''} />
      <Flex direction="column" align="start" className={styles.container}>
        {renderHeader()}
        {renderContent()}
      </Flex>
    </Flex>
  );
});

Bg_3Grid.defaultProps = defaultProps;
Bg_3Grid.propTypes = propTypes;

export default Bg_3Grid;



// WEBPACK FOOTER //
// ./src/pubBiz/CommonCell/ClassifyCell/components/BG_3Grid/index.js